import { Vector2Tuple } from 'three';
import { Matrix3, Vector2 } from 'three';

import { split } from '@spazious/dollhouse-selector';
import {
  createAddNodeAction,
  createUpdateNodeAction,
  FShallowNodeTypesEnum,
  FShallowNodeWithSelector,
} from '@spazious/dollhouse-shallow-graph';

import { Commander, CommanderActionTypesEnum } from '../types.js';
import { generateSelector } from '../utils/generateSelector.js';

import { GraphNodeAction } from './GraphNodeAction.js';

const _position = new Vector2();

export type TranslateActionPayload = {
  /**
   * Relative translation of the node from the current position
   */
  movement: Vector2Tuple;
  /**
   * Whether to copy the node or not which mean to create a new node with
   * the same properties of the original node
   */
  copy?: boolean;

  /**
   * Whether to move stacked nodes as a group
   */
  asGroup?: boolean;
};

export class TranslateAction extends GraphNodeAction<
  CommanderActionTypesEnum.Translate,
  TranslateActionPayload
> {
  constructor(commander: Commander, selector: string, payload?: TranslateActionPayload) {
    super(commander, CommanderActionTypesEnum.Translate, selector, payload);
  }

  _apply(): void {
    const { staging, prevState } = this.commander;

    if (!this.payload) {
      throw new Error('Payload is required for TranslateAction');
    }

    const { movement, copy = false, asGroup = false } = this.payload;

    const selectors = split(this.selector());
    const translation = new Matrix3().translate(movement[0], movement[1]);

    staging.abort();

    let cursor = 0;

    selectors.forEach((selector) => {
      const node = prevState.nodes[selector];

      if (!node) {
        throw new Error(`Node with selector "${selector}" not found`);
      }

      const nodePosition = _position
        .fromArray((node as any).position ?? [0, 0])
        .applyMatrix3(translation)
        .toArray();

      if (copy) {
        // we omit meta until we change the way of managing assignaments
        // eslint-disable-next-line unused-imports/no-unused-vars, no-unused-vars
        const { meta, ...shallowNode } = node as any;
        const newSelector = generateSelector(++cursor, prevState.cursor);
        staging.add(
          createAddNodeAction(newSelector, {
            ...shallowNode,
            position: nodePosition,
          })
        );
      } else {
        let targetNodes: FShallowNodeWithSelector[] = [];
        if (asGroup && node.type !== FShallowNodeTypesEnum.FGroup) {
          // collect all related nodes
          const baseNodeSelector = node.parent ? node.parent : selector;
          targetNodes = Object.entries(prevState.nodes).reduce((acc, entry) => {
            const [nodeSelector, node] = entry;
            if (
              nodeSelector === baseNodeSelector ||
              (node.type !== FShallowNodeTypesEnum.FGroup && node.parent === baseNodeSelector)
            ) {
              return [...acc, { ...node, selector: nodeSelector }];
            }
            return acc;
          }, [] as FShallowNodeWithSelector[]);
        } else {
          // only current node
          targetNodes = [{ ...node, selector }];
        }

        targetNodes.forEach((node) => {
          const nodePosition = _position
            .fromArray((node as any).position ?? [0, 0])
            .applyMatrix3(translation)
            .toArray();
          staging.add(createUpdateNodeAction(node.selector, 'position', nodePosition));
        });
      }
    });
  }
}
